import React, { useState, useEffect, Fragment } from 'react';
import { Container, Row, Col, Card, CardHeader, CardBody, Table, Badge, Input, Collapse } from 'reactstrap';
import { Breadcrumbs } from '../../../../AbstractElements';
import axios from 'axios';
import API_BASE_URL from '../../../../Config/api';
import { toast } from 'react-toastify';
import { ChevronDown, ChevronRight, ExternalLink } from 'react-feather';

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatCurrency = (amount, currency) => {
  if (!amount && amount !== 0) return '-';
  const num = parseFloat(amount);
  const symbol = currency === 'USD' ? '$' : currency === 'GBP' ? '\u00A3' : '\u20AC';
  return `${symbol}${num.toFixed(2)}`;
};

const CreditNotes = () => {
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, used, unused
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    fetchCreditNotes();
  }, []);

  const fetchCreditNotes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/credit-notes`);
      setCreditNotes(response.data.creditNotes || []);
    } catch (err) {
      console.error('Error fetching credit notes:', err);
      toast.error('Failed to fetch credit notes');
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const filteredNotes = creditNotes.filter(cn => {
    // Status filter
    if (filterStatus === 'used' && !cn.used_for_invoice) return false;
    if (filterStatus === 'unused' && cn.used_for_invoice) return false;

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        (cn.credit_note_number || '').toLowerCase().includes(term) ||
        (cn.store_name || '').toLowerCase().includes(term) ||
        (cn.original_order_id || '').toLowerCase().includes(term) ||
        (cn.used_for_invoice || '').toLowerCase().includes(term)
      );
    }
    return true;
  });

  const totalAmount = filteredNotes.reduce((sum, cn) => sum + parseFloat(cn.amount || 0), 0);
  const usedCount = filteredNotes.filter(cn => cn.used_for_invoice).length;
  const unusedCount = filteredNotes.filter(cn => !cn.used_for_invoice).length;

  return (
    <Fragment>
      <Breadcrumbs parent="Orders" title="Credit Notes" />
      <Container fluid={true}>
        {/* Summary Cards */}
        <Row>
          <Col xl="3" sm="6">
            <Card className="o-hidden">
              <CardBody style={{ backgroundColor: '#7366ff', padding: '20px' }}>
                <div className="media-body">
                  <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>Total Credit Notes</span>
                  <h4 style={{ color: '#fff', fontSize: '28px', fontWeight: 600, marginBottom: 0 }}>
                    {filteredNotes.length}
                  </h4>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col xl="3" sm="6">
            <Card className="o-hidden">
              <CardBody style={{ backgroundColor: '#51bb25', padding: '20px' }}>
                <div className="media-body">
                  <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>Total Amount</span>
                  <h4 style={{ color: '#fff', fontSize: '24px', fontWeight: 600, marginBottom: 0 }}>
                    {'\u20AC'}{totalAmount.toFixed(2)}
                  </h4>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col xl="3" sm="6">
            <Card className="o-hidden">
              <CardBody style={{ backgroundColor: '#f73164', padding: '20px' }}>
                <div className="media-body">
                  <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>Used</span>
                  <h4 style={{ color: '#fff', fontSize: '28px', fontWeight: 600, marginBottom: 0 }}>
                    {usedCount}
                  </h4>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col xl="3" sm="6">
            <Card className="o-hidden">
              <CardBody style={{ backgroundColor: '#ff9f40', padding: '20px' }}>
                <div className="media-body">
                  <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>Unused</span>
                  <h4 style={{ color: '#fff', fontSize: '28px', fontWeight: 600, marginBottom: 0 }}>
                    {unusedCount}
                  </h4>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Row>
          <Col sm="12">
            <Card>
              <CardBody>
                <Row className="align-items-center">
                  <Col md="4">
                    <Input
                      type="text"
                      placeholder="Search by CN #, store, order ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </Col>
                  <Col md="3">
                    <Input
                      type="select"
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                    >
                      <option value="all">All Credit Notes</option>
                      <option value="unused">Unused Only</option>
                      <option value="used">Used Only</option>
                    </Input>
                  </Col>
                  <Col md="5" className="text-end">
                    <span className="text-muted">
                      Showing {filteredNotes.length} of {creditNotes.length} credit notes
                    </span>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Credit Notes Table */}
        <Row>
          <Col sm="12">
            <Card>
              <CardHeader className="pb-0">
                <h5>All Credit Notes</h5>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center py-5">
                    <div className="loader-box" style={{ display: 'inline-block' }}>
                      <div className="loader-7" />
                    </div>
                    <p className="mt-2 text-muted">Loading credit notes...</p>
                  </div>
                ) : filteredNotes.length === 0 ? (
                  <p className="text-muted text-center py-4">No credit notes found</p>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-border-horizontal">
                      <thead>
                        <tr>
                          <th style={{ width: '30px' }}></th>
                          <th>Credit Note #</th>
                          <th>Store</th>
                          <th>Original Order</th>
                          <th>Amount</th>
                          <th>Date Created</th>
                          <th>Status</th>
                          <th>Applied To</th>
                          <th>PDF</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredNotes.map((cn) => (
                          <Fragment key={cn.id}>
                            <tr
                              style={{ cursor: cn.items && cn.items.length > 0 ? 'pointer' : 'default' }}
                              onClick={() => cn.items && cn.items.length > 0 && toggleRow(cn.id)}
                            >
                              <td>
                                {cn.items && cn.items.length > 0 && (
                                  expandedRows[cn.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />
                                )}
                              </td>
                              <td><strong>{cn.credit_note_number}</strong></td>
                              <td>{cn.store_name}</td>
                              <td>{cn.original_order_id || '-'}</td>
                              <td><strong>{formatCurrency(cn.amount, cn.currency)}</strong></td>
                              <td>{formatDisplayDate(cn.created_at)}</td>
                              <td>
                                {cn.used_for_invoice ? (
                                  <Badge color="secondary">Used</Badge>
                                ) : (
                                  <Badge color="success">Available</Badge>
                                )}
                              </td>
                              <td>
                                {cn.used_for_invoice ? (
                                  <span>{cn.used_for_invoice} <small className="text-muted">({formatDisplayDate(cn.used_date)})</small></span>
                                ) : '-'}
                              </td>
                              <td>
                                <a
                                  href={`${API_BASE_URL}/api/credit-note-pdf/${cn.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  title="Regenerate PDF"
                                >
                                  <ExternalLink size={16} />
                                </a>
                              </td>
                            </tr>
                            {cn.items && cn.items.length > 0 && (
                              <tr>
                                <td colSpan="9" style={{ padding: 0, border: 'none' }}>
                                  <Collapse isOpen={expandedRows[cn.id]}>
                                    <div style={{ padding: '10px 20px 10px 50px', backgroundColor: '#f8f9fa' }}>
                                      <strong className="mb-2 d-block" style={{ fontSize: '13px' }}>Cancelled Items:</strong>
                                      <Table size="sm" bordered style={{ backgroundColor: '#fff', marginBottom: 0 }}>
                                        <thead>
                                          <tr>
                                            <th>Collection</th>
                                            <th>Product</th>
                                            <th>SKU</th>
                                            <th>Size</th>
                                            <th>Qty</th>
                                            <th>Unit Price</th>
                                            <th>Total</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {cn.items.map((item, idx) => (
                                            <tr key={idx}>
                                              <td>{item.collection || '-'}</td>
                                              <td>{item.title || '-'}</td>
                                              <td>{item.sku || '-'}</td>
                                              <td>{item.size}</td>
                                              <td>{item.cancelled_qty}</td>
                                              <td>{formatCurrency(item.unit_price, cn.currency)}</td>
                                              <td>{formatCurrency(item.total_price, cn.currency)}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </Table>
                                    </div>
                                  </Collapse>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default CreditNotes;
